import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { useTranslation } from 'react-i18next';

// Actions
import { useRequestSessionsForCPQuery } from '../../redux/api/sessions';

// Helpers
import { getChartInterval, getDateTimeFormat } from '../../Utils';
import { getDatesForDateRange } from '../../Utils/charging-point';

// Components
import PeriodSelect from '../Utils/PeriodSelect';
import IfRender from '../Utils/IfRender';
import LoadingAnimation from '../Utils/BateryChargeAnimation';
import UsersTable from './usersTable';
import Chart from '../Utils/Chart';
import SessionsTable from './sessionsTable';
import NoData from '../Utils/NoData';

// Constants
import { COLOUR_CODES } from '../../Constants';

// Style
import './style.scss';
import 'react-tabs/style/react-tabs.css';
import { getDurationStringFromDates } from '../../Utils/time.js';
import { sessionsFilter } from '../../QueryFilters/queries.js';

const SessionsData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const cpId = urlParams.get('cp');
  const from = urlParams.get('from');
  const to = urlParams.get('to');
  const [usersSessions, setUsersSessions] = useState([]);
  const [chartDataSource, setChartDataSource] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const { t } = useTranslation();

  const [sessionsInterval, setSessionsInterval] = useState({
    start: from ? moment(from * 1000) : moment().startOf('day'),
    end: to ? moment(to * 1000) : moment().endOf('day')
  });

  const {
    data: { list: sessions = [] } = {},
    isLoading,
    isSuccess
  } = useRequestSessionsForCPQuery({
    itemsPerPage: 999999,
    chargePointId: cpId,

    filter: JSON.stringify(sessionsFilter(sessionsInterval.start, sessionsInterval.end))
  });
  useEffect(() => {
    isSuccess && prepareChartDataSource(selectedUser);
  }, [sessions, selectedUser]);

  const prepareChartDataSource = (selectedUser) => {
    const arr = [];
    const userArr = [];
    let filteredSessions = selectedUser
      ? sessions.filter((session) => session.userId === selectedUser)
      : sessions;

    sessions.forEach((data) => {
      // Users Table Data
      const userAlreadyExist = userArr.find((user) => user.id === data.userId);
      if (!userAlreadyExist) {
        userArr.push({
          id: data.userId,
          name: data.user?.userName,
          email: data.user?.email,
          role: data.user?.roles?.[0]?.name,
          session: [data],
          color: COLOUR_CODES[userArr.length],
          parentOrganization: data.user?.parentOrganization?.name
        });
      } else {
        userAlreadyExist.session.push(data);
        userAlreadyExist.id = userAlreadyExist.id || data.userId;
        userAlreadyExist.email = userAlreadyExist.email || data.user?.email;
        userAlreadyExist.role = userAlreadyExist.role || data.user?.roles?.[0]?.name;
        userAlreadyExist.parentOrganization =
          userAlreadyExist.parentOrganization || data.user?.parentOrganization?.name;
      }
    });

    filteredSessions.forEach((data, i) => {
      // Chart Data
      const startIso = data.createdAt || data.startDate || data.start_time;
      const endIso = data.endDate || data.stopDate || data.end_time;
      const start = startIso ? moment(startIso).valueOf() : NaN;
      const end = endIso ? moment(endIso).valueOf() : Date.now();
      if (!Number.isFinite(start)) {
        return; // skip invalid points
      }
      const durationMs = Math.max(0, end - start);
      const hours = durationMs / (1000 * 60 * 60);
      const getColor = userArr.find((user) => user.id === data.userId);

      // Get total energy from session (V2 now stores energy on session like V1)
      const totalEnergy = Number(
        data?.totalEnergyWh ??
        data?.totalEnergyConsumed ??
        data?.energy_consumption ??
        0
      );
      if (!Number.isFinite(totalEnergy)) {
        return; // skip invalid values
      }

      arr.push({
        data: [
          {
            x: start,
            y: totalEnergy
          }
        ],
        color: getColor?.color,
        name: data?.label || `session ${i + 1}`,
        pointWidth: Math.max(1, hours * 10)
      });
    });

    const sessionData = {
      title: t('sessions'),
      value: t('energy_consumption_kwh'),
      chartData: arr,
      legendColor: 'blue'
    };

    setUsersSessions(userArr);
    setChartDataSource([sessionData]);
  };

  const setTimePeriod = (start, end) => {
    setSessionsInterval({
      start: start,
      end: end
    });
  };

  const csvUserData = useMemo(() => {
    return [
      [
        'Username',
        'Email',
        'Role',
        'Sessions',
        'Parent Organization',
        'Total Energy Consumption (KWh)'
      ],
      ...usersSessions.map(({ name, email, role, session, parentOrganization }) => {
        let totalValueWh = 0;
        session?.forEach((val) => {
          const wh = Number(val.totalEnergyWh ?? val.totalEnergyConsumed ?? val.energy_consumption ?? 0);
          if (Number.isFinite(wh)) totalValueWh += wh;
        });
        return [
          name || '-',
          email || '-',
          role || '-',
          session?.length || 0,
          parentOrganization || '-',
          (totalValueWh / 1000) || 0
        ];
      })
    ];
  }, [usersSessions]);

  const csvSessionsData = useMemo(() => {
    return [
      [
        'Username',
        'Email',
        'Parent Organization',
        'Session Start',
        'Session End',
        'Duration',
        'Total Energy Consumption (KWh)',
        'Price per KW',
        'Session Price',
        'Label',
        'RFID Tag ID'
      ],
      ...sessions.map(({ user, session, label, rfid_tag_id, createdAt, endDate, price }) => {
        let totalValue = 0;
        // session?.forEach((val) => {
        //     totalValue += val.total_energy_consumed_in_mwh;
        // });
        return [
          user.username || '-',
          user.email || '-',
          // userInfo.parent_organization?.organization_name || '-',
          createdAt ? moment(createdAt).format('DD/MM/YYYY HH:mm') : '-',
          createdAt ? moment(createdAt).format('DD/MM/YYYY HH:mm') : '-',
          // createdAt && endDate ? getDurationStringFromDates(start_date, endDate) : '-',
          // (totalValue / 1000) || 0,
          // price,
          // price * ((totalValue / 1000) || 0),
          label || '-'
          // rfid_tag_id || '-'
        ];
      })
    ];
  }, [sessions]);

  const handleUserSelect = (userId) => {
    setSelectedUser(userId ? Number(userId) : null);
  };

  return (
    <div className='page-content'>
      <PeriodSelect sessionsInterval={sessionsInterval} setDates={setTimePeriod} />

      <IfRender condition={!isLoading} otherwise={<LoadingAnimation />}>
        <IfRender condition={sessions.length > 0} otherwise={<NoData />}>
          <Tabs>
            <TabList>
              <Tab>{t('chart')}</Tab>
              <Tab>{t('details')}</Tab>
            </TabList>

            <TabPanel>
              <Chart
                chartName={t('sessions')}
                type='column'
                data={chartDataSource}
                interval={getChartInterval(sessionsInterval)}
                format={getDateTimeFormat(sessionsInterval)}
              />
              <UsersTable
                csvData={csvUserData}
                selectedUser={selectedUser}
                setSelectedUser={handleUserSelect}
                tableData={usersSessions}
              />
            </TabPanel>

            <TabPanel>
              <SessionsTable tableData={sessions} csvData={csvSessionsData} />
            </TabPanel>
          </Tabs>
        </IfRender>
      </IfRender>
    </div>
  );
};

export default SessionsData;
